// File: src/components/AllCategoriesSection.jsx
// Main container for Amazon-style category discovery
// Displays ALL categories from marketplace in vertical stack
// Each category shows multiple subcategories with product previews
// Scalable architecture: works with 5 or 50 categories

import React, { useState, useEffect } from 'react';
import CategoryRowSection from './CategoryRowSection';

const AllCategoriesSection = ({ categories, loading = false, error = null }) => {
  const [isVisible, setIsVisible] = useState(false);

  // Fade-in animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="w-full bg-white px-4 py-12">
        <div className="container mx-auto">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
            <p className="text-gray-600 font-medium">Loading categories...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full bg-white px-4 py-12">
        <div className="container mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700 font-semibold mb-2">Failed to load categories</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!categories || categories.length === 0) {
    return (
      <div className="w-full bg-white px-4 py-12">
        <div className="container mx-auto text-center">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <p className="text-gray-600 font-medium">No categories available</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`w-full bg-white px-4 py-12 transition-opacity duration-700 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="container mx-auto">
        
        {/* Section Header */}
        {/* <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Shop by Category
          </h1>
          <p className="text-gray-600 text-lg">
            Discover products across {categories.length} categories
          </p>
          <div className="h-1 w-24 bg-blue-600 rounded-full mx-auto mt-4"></div>
        </div> */}

        {/* All Category Rows - Vertical Stack */}
        <div className="space-y-12">
          {categories.map((category) => (
            <CategoryRowSection
              key={category.id}
              category={category}
            />
          ))}
        </div>

        {/* Bottom CTA - Optional promotional section */}
        {categories.length > 5 && (
          <div className="mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 text-center border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Can't find what you're looking for?
            </h3>
            <p className="text-gray-600 mb-4">
              Use our search to explore thousands of products
            </p>
            <button 
              onClick={() => document.querySelector('input[type="search"]')?.focus()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              Search Products
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllCategoriesSection;
